import * as React from "react";
import type { VariantProps } from "class-variance-authority";

import type { AnalyticsElementId } from "@/lib/analytics/analyticsElementId";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/shared/ui/button-variants";
import { ExternalLink } from "@/shared/ui/external-link";
import { InternalLink } from "@/shared/ui/internal-link";

type CtaLinkProps = VariantProps<typeof buttonVariants> & {
  analyticsElementId?: AnalyticsElementId;
  className?: string;
  children?: React.ReactNode;
} & (
    | {
        /** An in-app route, navigated inside the SPA without reloading it. */
        to: string;
        href?: never;
      }
    | (React.AnchorHTMLAttributes<HTMLAnchorElement> & {
        /** A destination outside the SPA: another site, a scheduler, a mailto. */
        href: string;
        to?: never;
      })
  );

/**
 * Marketing CTA chrome — a button-styled link for the landing surfaces. `to` is
 * an in-app route and renders an `InternalLink`; `href` leaves the SPA through
 * an `ExternalLink`. Pass `analyticsElementId` explicitly per call site.
 */
export const CtaLink = ({
  analyticsElementId,
  variant = "marketing",
  size = "lg",
  className,
  children,
  ...destination
}: CtaLinkProps) => {
  const ctaClassName = cn(buttonVariants({ variant, size, className }), "text-inverse");

  if (destination.to !== undefined) {
    return (
      <InternalLink analyticsElementId={analyticsElementId} href={destination.to} className={ctaClassName}>
        {children}
      </InternalLink>
    );
  }

  return (
    <ExternalLink analyticsElementId={analyticsElementId} className={ctaClassName} {...destination}>
      {children}
    </ExternalLink>
  );
};
